import { TemplateConfig } from "@/interfaces/editor";
import { ScreenshotProvider } from "./ScreenshotProvider";
import { BtDiskFileUtils } from "./BtDiskFileUtils";
import { VirtualFileSystem } from "../mediaRepository/VirtualFileSystem";
import { SupportedFeature, SupportedFeatureOnWebValues } from "../SupportedFeature";
import { revive } from "@/shared/revive";
import { DateProvider } from "@/shared/DateProvider";

export class Project {
    projectId: string;
    templateConfig: TemplateConfig;
    isAcquiringData: boolean = false;
    features: SupportedFeature[] = [];

    // information was saved in 'editing-project' but not persisted into project folder yet
    // useful for cases where user decides to reload browser or exit tab for some reason
    isDraftMode: boolean = false;

    type = "Project";

    static fromState(d: { projectId: string; templateConfig?: TemplateConfig | null; features?: SupportedFeature[] | null }) {
        if (!d) {
            return null;
        }
        const p = new Project(d.projectId);
        p.templateConfig = (revive(d.templateConfig) ?? p.templateConfig) as TemplateConfig;
        p.features = (revive(d.features) ?? p.features) as SupportedFeature[];
        return p;
    }

    static fromJSON<T extends Project>(this: new (projectId: string) => T, data: any): T {
        if (!data) {
            return data as T;
        }

        if (data instanceof this) {
            return data as T;
        }

        const projectId = (data as { projectId?: string }).projectId;
        const instance = new this(projectId ?? "");

        for (const [key, value] of Object.entries(data)) {
            if (value === undefined) {
                continue;
            }

            if (key === "projectId") {
                (instance as any).projectId = value as string;
                continue;
            }

            if (key === "templateConfig") {
                instance.templateConfig = (revive(value) ?? instance.templateConfig) as TemplateConfig;
                continue;
            }

            (instance as any)[key] = revive(value);
        }

        return instance;
    }
    
    constructor(projectId: string) {
        this.projectId = projectId;
    }

    static get baseUrl(): URL {
        // const url = new URL(FileManager.default.temporaryDirectory.toString());
        const url = new URL('https://bazaart.com');
        url.pathname = `${url.pathname}projects`;
        return url;
    }

    get draftPath(): string {
         return this.getDraftPathForProjectId(this.projectId);
    }

    get configLayersPath(): string {
        return `${this.draftPath}/layers_config.json`;
    }

    get configProjectPath(): string {
        return `${this.draftPath}/project_config.json`;
    }

    get finalProjectDest(): string {
        return this.getDraftPathForProjectId(this.projectId)
    }

    async screenshotInBlobUrl(): Promise<string> {
        let url = ScreenshotProvider.screenshotProjectUrl(this.projectId, true);
        let record = await VirtualFileSystem.getInstance().get(url)
        return await record?.getBase64Url();
    }
    getDraftPathForProjectId(projectId: string): string | null {
        return `/projects/${projectId}`;
    }

    getProjectUrl(): URL {
        return new URL(`${Project.baseUrl}/${this.projectId}`);
    }

    async loadProjectConfig(): Promise<TemplateConfig | null> {
        let blobString = await (await VirtualFileSystem.getInstance().get(this.configProjectPath))?.getBase64Url()
        if (!blobString) {
            let project = Project.createDraft(this.projectId)
            return Promise.resolve(project.templateConfig);
        }
        let json = await this.loadJsonContent(blobString)
        if (!json) {
            let t = await (await VirtualFileSystem.getInstance().get(this.configProjectPath))?.getBase64Url()
        }
        let projectConfig = json['BtDraftObject'] as TemplateConfig
        // There's some kind of a scenario where metadata draftGuid doesn't fit the actual projectId being stored.
        projectConfig.draftGuid = this.projectId;
        return projectConfig
    }

    async loadLayersConfig(): Promise<any[] | null> {
        let blobString = await (await VirtualFileSystem.getInstance().get(this.configLayersPath))?.getBase64Url()
        let json = await this.loadJsonContent(blobString)
        return json as any[]
    }


    async loadJsonContent(blobUrl: string): Promise<any> {
        try {
            const response = await fetch(blobUrl);
            if (!response.ok) {
                throw new Error(`Failed to fetch data: ${response.statusText}`);
            }
    
            const text = await response.text();
            const json = JSON.parse(text, (key, value) => {
                if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/.test(value)) {
                    return new Date(value);
                }
                return value;
            });
    
            return json;
        } catch {
            return null;
        }
    }

    async cleanDuplicatedFiles(clearMemory: boolean){
        let allLayers = await this.loadLayersConfig();
        
        // @ts-ignore
        let layerIds = allLayers?.flatMap((a) => Object.values(a)).map((l) => l?.bazaartGuid) ?? []
        await BtDiskFileUtils.filterDeletedLayers(this.getProjectUrl().pathname, layerIds, clearMemory)
        await BtDiskFileUtils.keepOnlyLatestVersion(this.getProjectUrl().pathname, clearMemory)
    }

    isSupportedOnWeb(): boolean {
        let webFeatures = SupportedFeatureOnWebValues
        return this.features.every(f => webFeatures.includes(f));
    }

    /**
     * Cleans up all in-memory caches (blob URLs and base64) for this project's files.
     * Does NOT delete the records from IndexedDB - only cleans up in-memory caches.
     * Call this when switching projects or leaving the editor to free memory.
     */
    async cleanupMemory(): Promise<void> {
        await Project.cleanupMemoryForProjectId(this.projectId);
    }

    /**
     * Static version of cleanupMemory for when you only have the projectId.
     * Cleans up all in-memory caches for both saved and editing project paths.
     */
    static async cleanupMemoryForProjectId(projectId: string): Promise<void> {
        if (!projectId) return;
        const vfs = VirtualFileSystem.getInstance();
        
        // Clean saved project files: /projects/{projectId}
        await vfs.revokeAllBlobUrls(`/projects/${projectId}`);
        
        // Clean editing project files: /editing-project/{projectId}
        await vfs.revokeAllBlobUrls(`/editing-project/${projectId}`);
    }

    static createDraft(projectId: string): Project {
        let newDraft = new Project(projectId);
        let now = DateProvider.getUTCDate();
        newDraft.templateConfig = {
            canvasIsCircular: false,
            draftCreationDate: now,
            draftDate: now,
            draftGuid: projectId,
            draftTemplateCategoryId: 0,
            draftTitle: "",
            hasOpacity: false,
            isAnimatedDraft: false,
            isExtracted: false,
            minDraftVersion: 1,
            originId: 0,
            originalCanvasSize: { width: 1024, height: 1024 },
            parentId: 0,
            screenshotDate: now,
            version: 255,
            isMagicTemplate: false,
            isTemplateReels: false
        };
        return newDraft || new Project(projectId);
    }
}